/**
 * The tenancy half of the owner's Activity timeline — pure derivation, no
 * queries.
 *
 * src/lib/owner-activity.ts covers what happened to the owner AS A BILLING
 * COUNTERPARTY: invoices, documents, notes, onboarding, subscription. It
 * carries nothing about what happened in their apartments, so a lease being
 * signed, a deposit arriving or a rent payment landing showed up nowhere on
 * the owner's profile except as one line in the Email history card — and that
 * card is built strictly from real send rows, so an email that was never sent
 * (see src/lib/owner-payment-notify.ts for how that happened to rent) left the
 * event with no trace at all.
 *
 * This builder is the fix: the events render from the business facts
 * themselves, so the timeline is right whether or not the mail went out, and
 * the two questions stay separable — "did it happen" here, "were they told"
 * on the Email history card.
 *
 * Every event is a fact with its own real timestamp. Nothing is derived from
 * current state without a date to hang it on: an undated event has to be
 * placed somewhere in the order and the only honest place is nowhere. That is
 * why a confirmed move-out renders from `move_out_notice_at` (when it was
 * agreed) with the move-out date as its detail, rather than from
 * `move_out_date` itself — that column is when the tenant LEAVES, usually in
 * the future, and using it as the sort key would file the event under a day
 * that has not happened.
 *
 * Kept separate from the hook that fetches the rows, and returning
 * owner-activity.ts's own event shape so OwnerActivityTimeline renders both
 * halves unchanged.
 */
import {
  Banknote,
  CalendarClock,
  ClipboardCheck,
  FileText,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import { formatDate, formatEUR } from "@/lib/v2-format";
import type { OwnerActivityEvent } from "@/lib/owner-activity";

/**
 * Rent recurs monthly, so an owner with a dozen occupied units produces more
 * rent receipts than every other event type combined — a year of them would
 * bury the one-shot lifecycle events. Capped to the most recent N overall,
 * matching the equivalent cap on the Email history card.
 */
const RENT_RECEIPT_LIMIT = 24;

/** One tenancy in one of the owner's apartments. */
export type OwnerTenancyActivityBooking = {
  id: number;
  tenant_name: string | null;
  apartment_label: string | null;
  contract_signed_at: string | null;
  /** Latest paid deposit payment for the booking. */
  deposit_paid_at: string | null;
  key_picked_up_at: string | null;
  move_in_protocol_submitted_at: string | null;
  /** When notice was given — the dated fact behind a confirmed move-out. */
  move_out_notice_at: string | null;
  /** The day the tenant leaves. Detail only, never the sort key — see header. */
  move_out_date: string | null;
  move_out_protocol_submitted_at: string | null;
  key_returned_at: string | null;
};

/** One settled rent month. */
export type OwnerTenancyRentReceipt = {
  booking_id: number;
  paid_at: string;
  amount_cents: number | null;
  /** The month the rent was FOR, not when it landed. */
  due_date: string | null;
};

/** `YYYY-MM` → "September 2026", for the rent receipt's detail line. */
function monthLabel(dueDate: string | null): string | null {
  if (!dueDate) return null;
  const d = new Date(`${String(dueDate).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export function buildOwnerTenancyActivity(input: {
  bookings: OwnerTenancyActivityBooking[];
  rentReceipts: OwnerTenancyRentReceipt[];
}): OwnerActivityEvent[] {
  const out: OwnerActivityEvent[] = [];

  /** Drops anything undateable instead of sorting it to the epoch. */
  const push = (
    at: string | null | undefined,
    event: Omit<OwnerActivityEvent, "at">
  ) => {
    if (!at) return;
    const t = new Date(at).getTime();
    if (Number.isNaN(t)) return;
    out.push({ at, ...event });
  };

  const bookingById = new Map(input.bookings.map((b) => [b.id, b]));
  /** "Tenant · Apartment 1" — which tenancy an event belongs to. */
  const contextOf = (b: OwnerTenancyActivityBooking | undefined) =>
    [b?.tenant_name?.trim() || "Tenant", b?.apartment_label?.trim()]
      .filter(Boolean)
      .join(" · ");

  for (const b of input.bookings) {
    const context = contextOf(b);
    push(b.contract_signed_at, {
      icon: FileText,
      tone: "sky",
      title: "Lease agreement signed",
      context,
    });
    push(b.deposit_paid_at, {
      icon: ShieldCheck,
      tone: "emerald",
      title: "Security deposit received",
      context,
    });
    push(b.key_picked_up_at, {
      icon: KeyRound,
      tone: "teal",
      title: "Keys handed over",
      context,
    });
    push(b.move_in_protocol_submitted_at, {
      icon: ClipboardCheck,
      tone: "sky",
      title: "Move-in protocol submitted",
      context,
    });
    push(b.move_out_notice_at, {
      icon: CalendarClock,
      tone: "amber",
      title: "Move-out confirmed",
      detail: formatDate(b.move_out_date)
        ? `Moving out ${formatDate(b.move_out_date)}`
        : null,
      context,
    });
    push(b.move_out_protocol_submitted_at, {
      icon: ClipboardCheck,
      tone: "amber",
      title: "Move-out protocol submitted",
      context,
    });
    push(b.key_returned_at, {
      icon: KeyRound,
      tone: "slate",
      title: "Keys returned",
      context,
    });
  }

  // Newest first before the cap, so trimming drops the oldest months rather
  // than whichever ones the query happened to return last.
  const receipts = input.rentReceipts
    .filter((r) => !!r.paid_at)
    .sort((a, b) =>
      a.paid_at < b.paid_at ? 1 : a.paid_at > b.paid_at ? -1 : 0
    )
    .slice(0, RENT_RECEIPT_LIMIT);
  for (const r of receipts) {
    const month = monthLabel(r.due_date);
    const amount =
      r.amount_cents != null ? formatEUR(r.amount_cents / 100) : null;
    push(r.paid_at, {
      icon: Banknote,
      tone: "emerald",
      title: amount ? `Rent received — ${amount}` : "Rent received",
      detail: month ? `Rent for ${month}` : null,
      context: contextOf(bookingById.get(r.booking_id)),
    });
  }

  return out.sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
  );
}
